/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { createBaseErrorHandler } from 'src/core/utils/interceptors/errorHandlers/createBaseErrorHandler';

/**
 * Represents an error response object expected from a LUIS endpoint.
 */
export type EndpointErrorObject = {
    /**
     * The error code received from the endpoint.
     */
    statusCode: string;

    /**
     * The error message received from the endpoint.
     */
    message: string;
};

/**
 * Represents a handler for error objects that are returned from
 * a LUIS endpoint.
 */
export const createEndpointErrorHandler = () => {
    const baseActions = createBaseErrorHandler();

    return {
        ...baseActions,

        /**
         * Handles the error response returned from a LUIS endpoint.
         *
         * @param error The error object thrown.
         * @returns A formatted Error object.
         */
        handle: async (error: AxiosError<{ error: EndpointErrorObject }>) => {
            if (!error.response) {
                return baseActions.delegate(error);
            }

            const jsonError = error.response.data && error.response.data.error;
            const errorObject: Error = new Error();

            if (typeof jsonError !== 'object' || !('statusCode' in jsonError && 'message' in jsonError)) {
                return baseActions.delegate(error);
            }

            errorObject.name = `${error.response.status}`;
            errorObject.message = `${jsonError.statusCode}: ${jsonError.message}`;

            return errorObject;
        }
    };
};
